import Web3 from 'web3';
import math from 'mathjs';
import moment from 'moment';
import { isNumber, isPositiveNumber, isPositiveInteger, isDecimalLimit } from './validateUtil';
import ethereum_address from 'ethereum-address';

class CustomError extends Error {
  constructor(messageId, description, messageText) {
    super();
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, CustomError);
    }
    this.messageId = messageId;
    this.messageText = messageText;
    this.description = description;
  }
}

/**
 * General a random string.
 * @returns {string}
 */
export function guid() {
  let S4 = function() {
    return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
  };

  return S4() + S4() + '-' + S4() + '-' + S4() + '-' + S4() + '-' + S4() + S4() + S4();
}

/**
 * Max element of Array
 * @param arr
 * @returns {*}
 */
export function max(arr) {
  if (!arr || !(arr instanceof Array)) return arr;

  return Math.max.apply(Math, arr);
}

/**
 * Min element of Array
 * @param arr
 * @returns {*}
 */
export function min(arr) {
  if (!arr || !(arr instanceof Array)) return arr;

  return Math.min.apply(Math, arr);
}

export function precisionRound(number, precision) {
  const shift = (number, precision, reverseShift) => {
    if (reverseShift) {
      precision = -precision;
    }

    const numArray = String(number).split('e');

    return Number(numArray[0] + 'e' + (numArray[1] ? Number(numArray[1]) + precision : precision));
  };

  return shift(Math.round(shift(number, precision, false)), precision, true);
}

/**
 * Convert wei to Ether from string input
 * Keeping up to 6 decimal numbers
 * Cutting all trailing zero behind decimals
 * example:
 * 0.001000 -> 0.001
 * 0.001001 -> 0.001001
 * @param weiString
 * @param fractionDigits default decimal value is 6
 */
export function convertWeitoEther(weiString, fractionDigits = 6) {
  // remove e notion from string
  const val = math.bignumber(weiString).toFixed();
  return parseFloat(parseFloat(Web3.utils.fromWei(val, 'ether')).toFixed(fractionDigits));
}

export function convertEthertoWei(ether) {
  return Web3.utils.toWei(Number(parseFloat(ether.toString()).toFixed(18)).toString(), 'ether');
}

export function convertToFloat(value, fractionDigits = 6) {
  return parseFloat(parseFloat(value).toFixed(fractionDigits));
}

export function multiBigNum(val1, val2) {
  return math.multiply(math.bignumber(val1), math.bignumber(val2)).toFixed();
}

export function sumFloat(val1, val2) {
  return convertToFloat(parseFloat(val1) + parseFloat(val2));
}

export function subFloat(val1, val2) {
  return convertToFloat(parseFloat(val1) - parseFloat(val2));
}

/**
 * Set timeout with a milliseconds.
 * @param ms {number}
 * @returns {Promise}
 */
export function wait(ms) {
  return new Promise((resolve, reject) => setTimeout(resolve, ms));
}

/**
 * Get value object with nested property
 * @param obj
 * @param keys
 * @returns {*}
 */
export function getValue(obj, keys) {
  if (typeof keys !== 'string') return null;

  keys.split('.').forEach(key => {
    obj = obj[key];
  });

  return obj;
}

/**
 * Check string is a image data.
 * @param str {string}
 * @returns {number}
 */
export function isImageData(str) {
  const result = str.match(/data:image/gi) || [];

  return result.length;
}

/**
 * Convert date to string with format 'YYYY/MM/DD'
 * @param d {Date}
 * @returns {string}
 */
export function showDate(d) {
  if (!(d instanceof Date)) return '';

  const month = d.getMonth() + 1;

  return `${d.getFullYear()}/${month}/${d.getDate()}`;
}

/**
 * Convert unix timestamp to date
 * @param t {number}
 * @returns {Date}
 */
export function convertTimeToDate(t) {
  if (typeof t !== 'number') t = parseInt(t, 10);

  return new Date(1000 * t);
}

/**
 * Convert date to unix timestamp of begin day (hours: 0:0:0:0)
 * @param d {Date}
 * @returns {Date}
 */
export function getBeginDay(d) {
  if (!(d instanceof Date)) return d;

  const timeBeginDay = d.setHours(0, 0, 0, 0);

  return new Date(timeBeginDay);
}

/**
 * Convert date to unix timestamp of end day (hours: 0:0:0:0)
 * @param d {Date}
 * @returns {Date}
 */
export function getEndDay(d) {
  if (!(d instanceof Date)) return d;

  const timeEndDay = d.setHours(23, 59, 59, 999);

  return new Date(timeEndDay);
}

/**
 * Validate empty value
 * @param value
 * @param errorId
 */
export function validateEmptyValue(value, errorId = 'EF2002_Content') {
  if (typeof value === 'number') value = value.toString();

  if (typeof value === 'undefined' || !value.trim().length) {
    throw new CustomError(errorId, 'Value is require!');
  }
}

export function validateEqualValue(value, values, errorId = 'EF2002_Content') {
  if (values instanceof Array) {
    if (values.indexOf(value) < 0) {
      throw new CustomError(errorId, 'Value must in array !');
    }
  } else {
    if (value !== values) {
      throw new CustomError(errorId, `Value must equal ${values}`);
    }
  }
}

export function validateCodeNumber(value, errorId) {
  if (value.indexOf('_') >= 0) {
    throw new CustomError(errorId, 'Input not match!');
  }
}

export function validateEtherAddr(value, errorId) {
  if (!ethereum_address.isAddress(value)) {
    throw new CustomError(errorId, 'Input not match!');
  }
}

/**
 * Validate value with range
 * @param value
 * @param range
 * @param errorId
 */
export function validateValueRange(value, range, errorId) {
  if (range instanceof Array) {
    if (range.indexOf(value.length) < 0) {
      throw new CustomError(errorId, 'Value must in range !');
    }
  } else {
    if (value.length !== range) {
      throw new CustomError(errorId, `Value require ${range} characters`);
    }
  }
}

/**
 * Validate value with range
 * @param value
 * @param range
 * @param errorId
 */
export function validateValueBounded(value, min, max, errorId) {
  if (value.length < min || value.length > max) {
    throw new CustomError(errorId, 'Value must in range !');
  }
}

/**
 * Validate value with upper range
 * @param value
 * @param range
 * @param errorId
 */
export function validateUpperRange(value, range, errorId = 'EF2006_Content') {
  if (value.length > range) {
    throw new CustomError(errorId, `Value require less than ${range} characters`);
  }
}

export function validateNumber(value, errorId = 'EF2089_Content') {
  if (!isNumber(value)) {
    throw new CustomError(errorId, 'Value must is a number!');
  }
}

export function validateInputValue(callback) {
  try {
    callback();
    return { success: true };
  } catch (error) {
    let messageId = 'EF2001_Content';
    if (error.messageId) {
      messageId = error.messageId;
    }
    // if (error.messageText) {
    //   errorMessage = error.messageText;
    // }
    return { messageId };
  }
}

export function dateFromUnix(string) {
  return new Date(string * 1000);
}

export function dateJpTextFormat(date) {
  // let date = new Date(string * 1000);
  // console.log(`commonUtil.js - (88) - `, string);
  // console.log(`commonUtil.js - (88) - `, date);
  let y = date.getFullYear();
  let m = date.getMonth() + 1;
  let d = date.getDate();
  let w = date.getDay();
  let wNames = ['日', '月', '火', '水', '木', '金', '土'];

  if (m < 10) {
    m = '0' + m;
  }
  if (d < 10) {
    d = '0' + d;
  }

  // フォーマット整形済みの文字列を戻り値にする
  // return y + '年' + m + '月' + d + '日 (' + wNames[w] + ')';
  return y + '年' + m + '月' + d + '日';
}

export function dateEnFormat(dateValue, format = 'YYYY/M/D') {
  let timeLabel = '';
  const time = moment(dateValue);
  if (time.isValid()) {
    timeLabel = time.format(format);
  }
  return timeLabel;
}

export function getNotificationFromOrder(order) {
  try {
    let title = '';
    const appName = process.env.VUE_APP_NAME;
    const {
      orderId,
      status,
      productId,
      product,
      shippingAddress,
      quantity,
      shippingFee,
      shippingTime,
      totalPrice,
      txHash
    } = order;
    const shipAddress = JSON.parse(shippingAddress);
    const {
      address: deliveryAddress,
      receiverName,
      phoneNumber,
      zipcode: deliveryAddressCode,
      prefecture: deliveryCity
    } = shipAddress;
    const orderStatus = parseInt(status, 10);
    switch (orderStatus) {
      case 1:
        if (!txHash) {
          title = `${product.name}(注文ID: ${orderId}) が注文されました。`;
        } else {
          title = `${product.name}(注文ID: ${orderId}) が決済されました。`;
        }
        break;
      case 2:
        title = `${product.name}(注文ID: ${orderId}) が決済されました。`;
        break;
      case 3:
        title = `${product.name}(注文ID: ${orderId}) が出荷されました。`;
        break;
      case 4:
        title = `${product.name}(注文ID: ${orderId}) が受取確認されました。`;
        break;
      default:
        break;
    }

    const content = {
      orderId,
      productId,
      productName: product.name,
      priceUnit: product.price,
      quantity,
      shipFee: shippingFee,
      totalPrice,
      receiverName,
      phoneNumber,
      deliveryAddress,
      deliveryCity,
      deliveryAddressCode,
      txHash
    };

    const image = product.image[0] || null;

    return { title, content: JSON.stringify(content), image };
  } catch (error) {
    console.log(`commonUtil.js - (147) - `, error);
    return {};
  }
}

/**
 * onBlur action
 * @param {Object} t
 * @param {Array} refs
 */
export function onBlur(t, refs) {
  if (!t || !(refs instanceof Array) || !refs.length) return;

  for (let i = 0; i < refs.length; i++) {
    if (t[refs[i]] && t[refs[i]] instanceof Array) {
      for (let j = 0; j < t[refs[i]].length; j++) {
        t[refs[i]][j].onBlur();
      }
    } else if (t && refs[i] in t && t[refs[i]]) {
      t[refs[i]].onBlur();
    }
  }
}

/**
 * Check validate error
 * @param {Object} objs
 * @returns {boolean}
 */
export function checkError(objs) {
  let result = true;
  for (let key in objs) {
    if (objs[key] && objs[key] instanceof Array) {
      for (let i = 0; i < objs[key].length; i++) {
        if (objs[key][i] && objs[key][i].error) {
          result = false;
          break;
        }
      }
    } else if (objs instanceof Object && objs[key] && objs[key].error) {
      result = false;
      break;
    }
  }

  return result;
}

/**
 * Convert a value to postcode format.
 * @param value
 * @returns {string}
 */
export function convertValueToPostalCode(value) {
  if (!value) return '';
  if (typeof value !== 'string') value = value.toString();

  return value.length > 3 ? value.slice(0, 3) + '-' + value.slice(3, value.length) : value;
}

/**
 * Convert postcode format to value.
 * @param value
 * @returns {*}
 */
export function convertPostalCodeToValue(value) {
  if (!value) return '';
  if (typeof value !== 'string') value = value.toString();

  return value.length > 3 ? value.replace(/-/g, '') : value;
}

/**
 * Convert number to local string format.
 * @param {number} value
 * @returns {*}
 */
export function convertToLocalString(value) {
  if (!value || typeof value !== 'number') return value;

  return value > 999 ? value.toLocaleString() : value;
}

/**
 * Function get all days, weeks or months between two times
 * @param {*} startTime startTime
 * @param {*} endTime endTime
 * @param {*} diffConstant 1 get all days, 7 get all week, 30 get all month
 */
export const spliceTwoTimes = (startTime, endTime, diffConstant) => {
  if (!(startTime instanceof Date) || !(endTime instanceof Date) || typeof diffConstant !== 'number') {
    throw new Error('Invalid Parameter');
  }
  const ONE_DAY_MILISECONDS = 24 * 60 * 60 * 1000;
  const results = [];
  let currTime = startTime;
  let nextTime = endTime;
  const endTimeMiliseconds = endTime.getTime();
  let isLast = false;
  do {
    const currTimeMiliSeconds = currTime.getTime();
    if (endTimeMiliseconds - currTimeMiliSeconds < diffConstant * ONE_DAY_MILISECONDS) {
      isLast = true;
      nextTime = endTime;
    } else {
      nextTime = new Date(currTime.getTime() + diffConstant * ONE_DAY_MILISECONDS - 1);
    }
    const labels = `${currTime.getFullYear()}-${currTime.getMonth() + 1}-${currTime.getDate()}`;
    results[labels] = {
      value: 0,
      count: 0,
      labels
    };
    currTime = new Date(currTime.getTime() + diffConstant * ONE_DAY_MILISECONDS);
  } while (!isLast);
  return results;
};

/**
 * Function get all days, weeks or months between two times
 * @param {Array} data .Format [{ key_as_string: "2018-6-27", doc_count: 1, revenue: { value: 0 } }]
 * @param {Date} from
 * @param {Date} to
 * @param {Number} filterBy 1 get all days, 7 get all week, 30 get all month
 */
export const getPeriods = (data = null, from, to, filterBy = 1) => {
  if (!(from instanceof Date) || !(to instanceof Date) || from.getTime() > to.getTime() || typeof filterBy !== 'number')
    return null;

  const result = {
    orders: [],
    sales: [],
    labels: []
  };

  let temp = from;
  do {
    temp = new Date(temp.getTime() + filterBy * 24 * 3600 * 1000 - 1);

    result['orders'].push(0);
    result['sales'].push(0);
    result['labels'].push(
      `${temp.getFullYear()}/${formatFullDateString(temp.getMonth() + 1)}/${formatFullDateString(temp.getDate())}`
    );
  } while (temp.getTime() < to.getTime() || temp.getTime() === to.getTime());

  if (data && data instanceof Array && data.length && result && result.labels && result.labels.length) {
    for (let i = 0; i < data.length; i++) {
      const label = formatDateString(data[i].key_as_string);
      const index = result.labels.indexOf(label);

      if (index !== -1) {
        result['orders'][index] = data[i].doc_count ? data[i].doc_count : 0;
        result['sales'][index] = data[i].revenue && data[i].revenue.value ? data[i].revenue.value : 0;
      }
    }
  }
  return result;
};

/**
 * Get display labels for analytics
 * @param {Date} from
 * @param {Date} to
 * @param {Number} filterBy 1 get all days, 7 get all week, 30 get all month
 */
export const getDisplayLabels = (from, to, filterBy = 1) => {
  if (!(from instanceof Date) || !(to instanceof Date) || typeof filterBy !== 'number') return null;

  const result = [];

  let temp = from;
  do {
    temp = new Date(temp.getTime() + filterBy * 24 * 3600 * 1000 - 1);

    result.push(`${temp.getFullYear()}/${temp.getMonth() + 1}/${temp.getDate()}`);
  } while (temp.getTime() < to.getTime());

  return result;
};

/**
 * truncate text
 * @param {String} text
 * @param {Number} length
 * @param {String} clamp
 * @returns {String}
 */
export const truncate = (text, length, clamp = '...') => {
  if (text == null || typeof text === 'undefined') {
    return '';
  }
  return text.length > length ? text.slice(0, length) + clamp : text;
};

/**
 * Convert format as '2019-01-01' to '2019/01/01
 * @param str
 * @returns {*}
 */
export const formatDateString = str => {
  if ((typeof str === 'string' && str.length === 8) || (typeof str === 'string' && str.length === 9)) {
    const subs = str.split('-');
    str = `${subs[0]}-${formatFullDateString(subs[1])}-${formatFullDateString(subs[2])}`;
  }

  return str.replace(/-/g, '/');
};

/**
 * Convert format month/day as '1' to '01', '2' to '02', ...
 * @param {String/Number} value
 * @returns {*}
 */
export const formatFullDateString = value => {
  if (typeof value === 'number' && value < 10) return `0${parseInt(value)}`;
  if (typeof value === 'string' && value.length === 1) return `0${value}`;

  return value;
};

export function parseJSON(string) {
  try {
    return JSON.parse(string);
  } catch (error) {
    console.log(`commonUtil.js - Convert failed - ${string}`, error);
  }
  return [];
}

export function arraysEqual(_arr1, _arr2) {
  if (!Array.isArray(_arr1) || !Array.isArray(_arr2) || _arr1.length !== _arr2.length) return false;

  const arr1 = _arr1.concat().sort();
  const arr2 = _arr2.concat().sort();

  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) return false;
  }

  return true;
}

export function getCategoryName(id, categories) {
  if (!(categories instanceof Array)) return '';

  let name = '';
  const rootCategories = categories.filter(item => item.id === id);

  if (!rootCategories.length) {
    for (let i = 0; i < categories.length; i++) {
      const subCategories = categories[i].sub_categories.filter(item => item.id === id);

      if (subCategories.length) {
        name = subCategories[0].name;
        break;
      }
    }
  } else {
    name = rootCategories[0].name;
  }

  return name;
}

export function isLocalStorage() {
  try {
    return 'localStorage' in window && window['localStorage'] !== null;
  } catch (e) {
    console.log('The browser is not support localStorage.', e);
    return false;
  }
}

export function setLocalStorage(key, data) {
  if (!isLocalStorage()) return;

  if (typeof data === 'object') {
    localStorage.setItem(key, JSON.stringify(data));
  } else {
    localStorage.setItem(key, data);
  }
}

export function getLocalStorage(key, type = null) {
  if (!isLocalStorage()) return null;

  return type && type === 'json' ? JSON.parse(localStorage.getItem(key)) : localStorage.getItem(key);
}

export function escapeString(text) {
  return text.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&');
}
